import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const GA_MEASUREMENT_ID = 'G-FNL8L149BY';

/** Routes that should NOT have Google Analytics */
const PRIVATE_ROUTE_PREFIXES = [
  '/dashboard',
  '/profile',
  '/company',
  '/settings',
  '/team',
  '/appraisals',
  '/admin',
  '/leads',
  '/analytics',
  '/business',
  '/auth/',
  '/mileage',
  '/share/',
  '/engagement/',
];

function isPublicRoute(pathname: string): boolean {
  return !PRIVATE_ROUTE_PREFIXES.some(prefix => pathname.startsWith(prefix));
}

/**
 * Renders Google Analytics gtag.js on public pages only.
 * Place inside a Router context + HelmetProvider.
 */
export function GoogleAnalytics() {
  const location = useLocation();
  const isPublic = isPublicRoute(location.pathname);

  // Send pageview on route change (only for public pages)
  useEffect(() => {
    if (isPublic && typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('config', GA_MEASUREMENT_ID, {
        page_path: location.pathname + location.search,
      });
    }
  }, [location.pathname, location.search, isPublic]);

  if (!isPublic) return null;

  return (
    <Helmet>
      <script async src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`} />
      <script>{`
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${GA_MEASUREMENT_ID}');
      `}</script>
    </Helmet>
  );
}

/**
 * For member sites rendered via SubdomainSiteRouter (always public).
 */
export function GoogleAnalyticsMemberSite() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('config', GA_MEASUREMENT_ID, {
        page_path: location.pathname + location.search,
      });
    }
  }, [location.pathname, location.search]);

  return (
    <Helmet>
      <script async src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`} />
      <script>{`
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${GA_MEASUREMENT_ID}');
      `}</script>
    </Helmet>
  );
}
